import { PageHeader } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, Plus, Trash2, Sun, Settings, AlertCircle, Zap } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pricerSolarService } from '../services/pricer-solar.service';
import type {
  SolarCatalog,
  InverterOption,
  PanelOption,
  StructureOption,
} from '../types/pricer-solar.types';

export function PricerSolarConfigPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'COMPANY_ADMIN' || user?.role === 'PLATFORM_ADMIN';

  const [config, setConfig] = useState<SolarCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    pricerSolarService
      .getCatalog()
      .then(setConfig)
      .catch((err) =>
        toast({ title: 'Error al cargar', description: err?.message, variant: 'destructive' })
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = <K extends keyof SolarCatalog>(field: K, value: SolarCatalog[K]) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  const addCity = () => {
    if (!config) return;
    setConfig({
      ...config,
      cities: [...config.cities, { name: 'Nueva ciudad', hspHoursDay: 5.0 }],
    });
  };
  const removeCity = (idx: number) => {
    if (!config) return;
    setConfig({ ...config, cities: config.cities.filter((_, i) => i !== idx) });
  };
  const updateCity = (idx: number, field: 'name' | 'hspHoursDay', value: string | number) => {
    if (!config) return;
    const next = [...config.cities];
    next[idx] = { ...next[idx], [field]: value };
    setConfig({ ...config, cities: next });
  };

  const addPanel = () => {
    if (!config) return;
    setConfig({
      ...config,
      panelOptions: [
        ...config.panelOptions,
        { watts: 500, label: 'Nuevo', brand: '', model: '', priceUsd: undefined },
      ],
    });
  };
  const removePanel = (idx: number) => {
    if (!config) return;
    setConfig({ ...config, panelOptions: config.panelOptions.filter((_, i) => i !== idx) });
  };
  const updatePanel = <K extends keyof PanelOption>(
    idx: number,
    field: K,
    value: PanelOption[K]
  ) => {
    if (!config) return;
    const next = [...config.panelOptions];
    next[idx] = { ...next[idx], [field]: value };
    setConfig({ ...config, panelOptions: next });
  };

  const addInverter = () => {
    if (!config) return;
    setConfig({
      ...config,
      inverterCatalog: [
        ...config.inverterCatalog,
        {
          brand: 'Genérico',
          model: 'Nuevo',
          descripcion: '',
          tipo: 'On Grid',
          fase: 'Monofásico',
          kw: 5,
          priceUsd: undefined,
        },
      ],
    });
  };
  const removeInverter = (idx: number) => {
    if (!config) return;
    setConfig({
      ...config,
      inverterCatalog: config.inverterCatalog.filter((_, i) => i !== idx),
    });
  };
  const updateInverter = <K extends keyof InverterOption>(
    idx: number,
    field: K,
    value: InverterOption[K]
  ) => {
    if (!config) return;
    const next = [...config.inverterCatalog];
    next[idx] = { ...next[idx], [field]: value };
    setConfig({ ...config, inverterCatalog: next });
  };

  const addStructure = () => {
    if (!config) return;
    setConfig({
      ...config,
      structureCatalog: [
        ...config.structureCatalog,
        { name: 'Nueva', priceUsd: 0, panelsPerKit: 4 },
      ],
    });
  };
  const removeStructure = (idx: number) => {
    if (!config) return;
    setConfig({
      ...config,
      structureCatalog: config.structureCatalog.filter((_, i) => i !== idx),
    });
  };
  const updateStructure = <K extends keyof StructureOption>(
    idx: number,
    field: K,
    value: StructureOption[K]
  ) => {
    if (!config) return;
    const next = [...config.structureCatalog];
    next[idx] = { ...next[idx], [field]: value };
    setConfig({ ...config, structureCatalog: next });
  };

  const handleSave = async () => {
    if (!config) return;
    if (config.cities.length === 0) {
      toast({ title: 'Necesitás al menos una ciudad', variant: 'destructive' });
      return;
    }
    if (config.panelOptions.length === 0) {
      toast({ title: 'Necesitás al menos un panel', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const fresh = await pricerSolarService.updateConfig(config);
      setConfig(fresh);
      toast({ title: 'Configuración guardada', variant: 'success' });
    } catch (err: any) {
      toast({
        title: 'Error al guardar',
        description: err?.response?.data?.error || err?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8 max-w-xl">
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <AlertCircle className="w-12 h-12 mx-auto text-alerta" />
              <h2 className="text-xl font-bold">Acceso restringido</h2>
              <p className="text-muted-foreground">
                Sólo los administradores de la empresa pueden editar los parámetros del Cotizador
                Solar.
              </p>
              <Button onClick={() => navigate('/pricer-solar')} variant="outline">
                Volver al Cotizador
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (rediseño, 19-oct). */}
        <PageHeader
          modulo="cotiza"
          etiqueta="Cotiza · Cotizador Solar"
          icono={Settings}
          titulo="Parámetros del Cotizador Solar"
          subtitulo="Constantes usadas en cada cálculo. Los cambios aplican a las cotizaciones nuevas."
          volver={{ texto: 'Volver al Cotizador Solar', onClick: () => navigate('/pricer-solar') }}
          acciones={
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-4 w-4" />
              )}
              Guardar cambios
            </Button>
          }
        />

        {/* Constantes financieras */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Constantes financieras y técnicas</CardTitle>
            <CardDescription>
              Valores que el motor de cálculo usa por defecto para todos los SKU.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field
                label="Precio kWh (Bs)"
                hint="Precio de la energía por omisión"
                value={config.precioKwhBs}
                step={0.01}
                onChange={(v) => update('precioKwhBs', v)}
              />
              <Field
                label="USD/kWp instalado"
                hint="Precio referencia mercado"
                value={config.precioKwInstaladoUsd}
                step={50}
                onChange={(v) => update('precioKwInstaladoUsd', v)}
              />
              <Field
                label="Tipo de cambio (BOB/USD)"
                hint="Para conversiones"
                value={config.tipoCambioBob}
                step={0.01}
                onChange={(v) => update('tipoCambioBob', v)}
              />
              <Field
                label="IVA (decimal)"
                hint="0.13 = 13%"
                value={config.iva}
                step={0.01}
                onChange={(v) => update('iva', v)}
              />
              <Field
                label="Factor de rendimiento"
                hint="Rendimiento del sistema (0–1)"
                value={config.factorRendimiento}
                step={0.01}
                onChange={(v) => update('factorRendimiento', v)}
              />
              <Field
                label="Degradación anual"
                hint="0.005 = 0.5%/año"
                value={config.degradacionAnual}
                step={0.001}
                onChange={(v) => update('degradacionAnual', v)}
              />
              <Field
                label="Factor de generación"
                hint="Factor neto (0–1)"
                value={config.factorGeneration}
                step={0.01}
                onChange={(v) => update('factorGeneration', v)}
              />
              <Field
                label="kg CO₂ / kWh"
                hint="Matriz energética local"
                value={config.emisionesCo2KgPerKwh}
                step={0.01}
                onChange={(v) => update('emisionesCo2KgPerKwh', v)}
              />
              <Field
                label="Margen inversor (kW)"
                hint="Sumado al kWp para auto-elegir"
                value={config.inverterSafetyMarginKw}
                step={0.5}
                onChange={(v) => update('inverterSafetyMarginKw', v)}
              />
              <Field
                label="Crecimiento sistema"
                hint="0.2 = 20% extra de paneles"
                value={config.crecimientoSistema}
                step={0.05}
                onChange={(v) => update('crecimientoSistema', v)}
              />
              <Field
                label="% Mano de obra"
                hint="0.5 = 50% del hardware"
                value={config.pctServiciosInstalacion}
                step={0.05}
                onChange={(v) => update('pctServiciosInstalacion', v)}
              />
              <Field
                label="% Materiales adic."
                hint="0.7 = 70% del hardware"
                value={config.pctMateriales}
                step={0.05}
                onChange={(v) => update('pctMateriales', v)}
              />
              <Field
                label="% Gastos extras"
                hint="Sobre (mano de obra + materiales)"
                value={config.pctGastosExtras}
                step={0.05}
                onChange={(v) => update('pctGastosExtras', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Ciudades */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Ciudades / HSP</CardTitle>
              <CardDescription>
                Horas Sol Pico (h/día) por ciudad. Aparecen en el selector del wizard.
              </CardDescription>
            </div>
            <Button onClick={addCity} size="sm" variant="outline">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Agregar ciudad
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config.cities.map((city, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_140px_auto] gap-2 items-center">
                  <Input
                    value={city.name}
                    onChange={(e) => updateCity(idx, 'name', e.target.value)}
                    placeholder="Nombre de la ciudad"
                  />
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.1"
                      min={0}
                      value={city.hspHoursDay}
                      onChange={(e) =>
                        updateCity(idx, 'hspHoursDay', parseFloat(e.target.value) || 0)
                      }
                      className="pr-14"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      h/día
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCity(idx)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {config.cities.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sin ciudades. Agregá al menos una.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Paneles */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Sun className="w-4 h-4 text-alerta" />
                Opciones de panel solar
              </CardTitle>
              <CardDescription>
                Potencias disponibles para seleccionar en el wizard.
              </CardDescription>
            </div>
            <Button onClick={addPanel} size="sm" variant="outline">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Agregar panel
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-[120px_1fr_1fr_1fr_120px_auto] gap-2 text-xs text-muted-foreground font-semibold px-1">
                <span>Watts</span>
                <span>Etiqueta</span>
                <span>Marca</span>
                <span>Modelo</span>
                <span>Precio USD</span>
                <span></span>
              </div>
              {config.panelOptions.map((panel, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_1fr_120px_auto] gap-2 items-center"
                >
                  <div className="relative">
                    <Input
                      type="number"
                      step={10}
                      min={0}
                      value={panel.watts}
                      onChange={(e) => updatePanel(idx, 'watts', parseInt(e.target.value) || 0)}
                      className="pr-9"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      W
                    </span>
                  </div>
                  <Input
                    value={panel.label}
                    onChange={(e) => updatePanel(idx, 'label', e.target.value)}
                    placeholder="Etiqueta (Ej: Estándar)"
                  />
                  <Input
                    value={panel.brand ?? ''}
                    onChange={(e) => updatePanel(idx, 'brand', e.target.value)}
                    placeholder="Marca (Ej: Jinko)"
                  />
                  <Input
                    value={panel.model ?? ''}
                    onChange={(e) => updatePanel(idx, 'model', e.target.value)}
                    placeholder="Modelo"
                  />
                  <div className="relative">
                    <Input
                      type="number"
                      step={1}
                      min={0}
                      value={panel.priceUsd ?? ''}
                      onChange={(e) =>
                        updatePanel(
                          idx,
                          'priceUsd',
                          e.target.value === '' ? undefined : parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="Opcional"
                      className="pr-10"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      USD
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePanel(idx)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {config.panelOptions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sin paneles. Agregá al menos uno.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Catálogo de Inversores */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-info" />
                Catálogo de inversores
              </CardTitle>
              <CardDescription>
                El cálculo elige automáticamente el inversor más chico que cumple{' '}
                <span className="font-mono">kW ≥ kWp + margen</span>. Si está vacío, usa la regla
                escalonada genérica.
              </CardDescription>
            </div>
            <Button onClick={addInverter} size="sm" variant="outline">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Agregar inversor
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-[1fr_1fr_120px_140px_140px_100px_120px_auto] gap-2 text-xs text-muted-foreground font-semibold px-1">
                <span>Marca</span>
                <span>Modelo</span>
                <span>kW</span>
                <span>Tipo</span>
                <span>Fase</span>
                <span>Precio USD</span>
                <span>Descripción</span>
                <span></span>
              </div>
              {config.inverterCatalog.map((inv, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_140px_140px_100px_120px_auto] gap-2 items-center"
                >
                  <Input
                    value={inv.brand}
                    onChange={(e) => updateInverter(idx, 'brand', e.target.value)}
                    placeholder="Marca"
                  />
                  <Input
                    value={inv.model}
                    onChange={(e) => updateInverter(idx, 'model', e.target.value)}
                    placeholder="Modelo"
                  />
                  <div className="relative">
                    <Input
                      type="number"
                      step={0.5}
                      min={0}
                      value={inv.kw}
                      onChange={(e) => updateInverter(idx, 'kw', parseFloat(e.target.value) || 0)}
                      className="pr-10"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      kW
                    </span>
                  </div>
                  <Select
                    value={inv.tipo}
                    onValueChange={(v) => updateInverter(idx, 'tipo', v as InverterOption['tipo'])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On Grid">On Grid</SelectItem>
                      <SelectItem value="Híbrido">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={inv.fase}
                    onValueChange={(v) => updateInverter(idx, 'fase', v as InverterOption['fase'])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monofásico">Monofásico</SelectItem>
                      <SelectItem value="Trifásico">Trifásico</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step={1}
                    min={0}
                    value={inv.priceUsd ?? ''}
                    onChange={(e) =>
                      updateInverter(
                        idx,
                        'priceUsd',
                        e.target.value === '' ? undefined : parseFloat(e.target.value) || 0
                      )
                    }
                    placeholder="Opc."
                  />
                  <Input
                    value={inv.descripcion ?? ''}
                    onChange={(e) => updateInverter(idx, 'descripcion', e.target.value)}
                    placeholder="Descripción"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeInverter(idx)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {config.inverterCatalog.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sin inversores en el catálogo. Sin entradas, se usa la regla escalonada genérica
                  (3/5/6/10 kW).
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Catálogo de Estructuras */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Catálogo de estructuras de montaje</CardTitle>
              <CardDescription>
                Kits de montaje por tipo de techo. La cantidad se calcula como{' '}
                <span className="font-mono">ceil(paneles / panelsPerKit)</span>.
              </CardDescription>
            </div>
            <Button onClick={addStructure} size="sm" variant="outline">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Agregar estructura
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-[1fr_140px_140px_auto] gap-2 text-xs text-muted-foreground font-semibold px-1">
                <span>Nombre</span>
                <span>Precio USD/kit</span>
                <span>Paneles/kit</span>
                <span></span>
              </div>
              {config.structureCatalog.map((s, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_auto] gap-2 items-center"
                >
                  <Input
                    value={s.name}
                    onChange={(e) => updateStructure(idx, 'name', e.target.value)}
                    placeholder="Loza / Teja Colonial / Calamina…"
                  />
                  <div className="relative">
                    <Input
                      type="number"
                      step={1}
                      min={0}
                      value={s.priceUsd}
                      onChange={(e) =>
                        updateStructure(idx, 'priceUsd', parseFloat(e.target.value) || 0)
                      }
                      className="pr-10"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      USD
                    </span>
                  </div>
                  <Input
                    type="number"
                    step={1}
                    min={1}
                    value={s.panelsPerKit}
                    onChange={(e) =>
                      updateStructure(idx, 'panelsPerKit', parseInt(e.target.value) || 1)
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStructure(idx)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {config.structureCatalog.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sin estructuras. Si está vacío, el wizard no muestra el selector y el desglose
                  omite esta línea.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reglas / info */}
        <Card className="bg-muted/30">
          <CardContent className="p-4 text-xs text-muted-foreground space-y-1.5">
            <p>
              <strong>Cómo se usan:</strong> el cálculo de un sistema toma estos parámetros del
              tenant. Las cotizaciones ya guardadas mantienen el snapshot del momento en que se
              calcularon — no se recalculan retroactivamente.
            </p>
            <p>
              <strong>Fórmulas afectadas:</strong> kWp = consumo / (30 × HSP × factorRendimiento) ·
              inversión = kWp × USD/kWp × TC · ahorro = kWp × HSP × 365 × factorGeneración × precio
              kWh · proyección con degradación anual.
            </p>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function Field({
  label,
  hint,
  value,
  step,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input
        type="number"
        step={step ?? 0.01}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="mt-1"
      />
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
